import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Users, Copy, Sparkles, Plus, Download, FileText } from "lucide-react";

export default function TeacherClass() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ name: "", school: "", grade: "" });

  const load = () => http.get("/classes/mine").then((r) => setClasses(r.data));

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.user_type !== "teacher" && user.user_type !== "admin") {
      toast.error(lang === "fr" ? "Réservé aux enseignants" : "Teachers only");
      nav("/");
      return;
    }
    load();
  }, [user, loading, nav, lang]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await http.post("/classes", form);
      setForm({ name: "", school: "", grade: "" });
      toast.success(lang === "fr" ? "Classe créée" : "Class created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const openClass = async (c) => {
    setSelected(c);
    const { data } = await http.get(`/classes/${c.id}/students`);
    setStudents(data.students || []);
  };

  const exportCsv = async (c) => {
    try {
      const resp = await http.get(`/classes/${c.id}/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `proxijob_class_${c.name.replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "fr" ? "CSV téléchargé" : "CSV downloaded");
    } catch (e) { toast.error("Error"); }
  };

  const exportPdf = async (c) => {
    try {
      const resp = await http.get(`/classes/${c.id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `proxijob_classe_${c.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(lang === "fr" ? "PDF téléchargé" : "PDF downloaded");
    } catch (e) { toast.error("Error"); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 data-testid="teacher-title" className="font-display text-3xl font-bold tracking-tight text-[#1E3A8A]">
          {lang === "fr" ? "Espace enseignant" : "Teacher space"}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {lang === "fr" ? "Créez vos classes, invitez vos élèves avec un code et suivez leur insertion pro." : "Create classes, invite students with a code, track their pro insertion."}
        </p>

        <form onSubmit={create} className="mt-8 grid md:grid-cols-4 gap-3 rounded-2xl bg-white border border-slate-200 p-5">
          <Input data-testid="class-name" placeholder={lang === "fr" ? "Nom de la classe" : "Class name"} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="class-school" placeholder={lang === "fr" ? "École (optionnel)" : "School (optional)"} value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} />
          <Input data-testid="class-grade" placeholder={lang === "fr" ? "Niveau (Terminale...)" : "Grade"} value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
          <Button data-testid="class-create" type="submit" className="bg-[#F97316] hover:bg-[#EA580C] rounded-full">
            <Plus className="w-4 h-4 mr-1.5" /> {lang === "fr" ? "Créer" : "Create"}
          </Button>
        </form>

        <div className="mt-8 grid md:grid-cols-[320px_1fr] gap-6">
          <aside className="space-y-3">
            {classes.map((c) => (
              <button
                key={c.id}
                data-testid={`class-item-${c.id}`}
                onClick={() => openClass(c)}
                className={`w-full text-left rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${
                  selected?.id === c.id ? "border-[#1E3A8A] bg-[#1E3A8A]/5" : "border-slate-200 bg-white"
                }`}
              >
                <p className="font-display font-semibold">{c.name}</p>
                <p className="text-xs text-slate-500">{c.school || "—"} · {c.grade || "—"}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Code :</span>
                  <code className="text-sm font-mono font-bold text-[#F97316]">{c.join_code}</code>
                  <button
                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(c.join_code); toast.success("Copié !"); }}
                    className="text-slate-400 hover:text-[#1E3A8A]"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">{c.students?.length || 0} {lang === "fr" ? "élèves" : "students"}</p>
              </button>
            ))}
            {classes.length === 0 && (
              <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-2xl">
                <GraduationCap className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">{lang === "fr" ? "Aucune classe" : "No class yet"}</p>
              </div>
            )}
          </aside>

          <section>
            {selected ? (
              <div>
                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#1E3A8A]" />
                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">
                      {students.length} {lang === "fr" ? "élèves dans " : "students in "} « {selected.name} »
                    </p>
                  </div>
                  <Button data-testid="class-export-csv" onClick={() => exportCsv(selected)} size="sm" variant="outline" className="rounded-full border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Exporter CSV" : "Export CSV"}
                  </Button>
                  <Button data-testid="class-export-pdf" onClick={() => exportPdf(selected)} size="sm" className="rounded-full bg-[#F97316] hover:bg-[#EA580C]">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> {lang === "fr" ? "Rapport PDF" : "PDF report"}
                  </Button>
                </div>
                <div className="space-y-3">
                  {students.map((s) => (
                    <div key={s.user_id} data-testid={`student-${s.user_id}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div>
                          <p className="font-semibold">{s.name}</p>
                          <p className="text-xs text-slate-500">{s.email}</p>
                          {s.diploma && <p className="text-sm text-[#1E3A8A] mt-1">{s.diploma} {s.specialty && `· ${s.specialty}`}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Badge className="bg-[#1E3A8A] text-white rounded-full">
                            {s.applications_count} {lang === "fr" ? "candidatures" : "apps"}
                          </Badge>
                          <Badge className="bg-[#F97316] text-white rounded-full">
                            <Sparkles className="w-3 h-3 mr-1" /> {s.roadmaps_count} roadmaps
                          </Badge>
                          {s.cv_filename && <Badge variant="secondary" className="rounded-full">CV ✓</Badge>}
                        </div>
                      </div>
                      {s.skills?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {s.skills.slice(0, 6).map((sk) => <Badge key={sk} variant="secondary" className="rounded-full text-[10px]">{sk}</Badge>)}
                        </div>
                      )}
                    </div>
                  ))}
                  {students.length === 0 && (
                    <div className="rounded-2xl bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/30 p-6 text-center">
                      <p className="text-sm text-slate-700">
                        {lang === "fr" ? "Partagez le code" : "Share the code"} <b className="text-[#C2410C]">{selected.join_code}</b> {lang === "fr" ? "avec vos élèves. Ils s'inscrivent sur Proxijob et rejoignent votre classe depuis leur profil." : "with your students."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl bg-white border border-dashed border-slate-200 p-10 text-center">
                <GraduationCap className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">{lang === "fr" ? "Sélectionnez une classe pour voir les élèves." : "Select a class to see students."}</p>
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
